import { ATTACHMENTS_UPLOADER_PRESET } from 'components/common/graphicUploader/attachmentsPreset';
import { CONFIG_TYPE, GU } from 'components/common/graphicUploader/types';
import { createInspectionTemplateGeneralNormDocumentsBaseUrl } from 'shared/domain/template/templateDocumentPaths';
import {
  calculateOrderBefore,
  calculateOrderBetween,
  createTemplateDraftChecklistItem,
  createTemplateDraftSection,
  generateOrder,
} from 'shared/domain/template/templateWizardDraft';
import {
  ChecklistItemError,
  ChecklistItemValidationFocus,
  CreateInspectionTemplateErrors,
  FormChecklistItem,
  FormSection,
  SectionError,
} from './types';
import {
  findFirstChecklistValidationFocus,
  summaryValidation,
} from './validation';
import { Identificable } from 'shared/types/commonView';
import { ErrorPresentation } from 'helpers/validators';

interface FormEntityIdentifiable {
  _id?: string;
  uniqueKeyId: string;
}

export function matchesFormEntityId<T extends FormEntityIdentifiable>(
  entity: T,
  id: string
): boolean {
  return entity._id === id || entity.uniqueKeyId === id;
}

interface FormEntityWithGraphicUploader extends FormEntityIdentifiable {
  graphicUploader?: GU;
}

export function indexGraphicUploadersByEntityId<
  T extends FormEntityWithGraphicUploader,
>(entities: readonly T[]): Record<string, GU> {
  return entities.reduce<Record<string, GU>>((acc, entity) => {
    if (!entity.graphicUploader) {
      return acc;
    }

    acc[entity._id || entity.uniqueKeyId] = entity.graphicUploader;
    return acc;
  }, {});
}

export function findFormEntityIndex<T extends FormEntityIdentifiable>(
  entities: T[],
  id: string
): number {
  return entities.findIndex((entity) => matchesFormEntityId(entity, id));
}

interface OrderedSibling {
  id: string;
  order: string;
}

export function calculateNewOrder(
  siblings: OrderedSibling[],
  beforeId?: string,
  afterId?: string
): string {
  const findOrder = (id: string): string | undefined =>
    siblings.find((s) => s.id === id)?.order;

  if (beforeId && afterId) {
    const beforeOrder = findOrder(beforeId);
    const afterOrder = findOrder(afterId);
    if (beforeOrder && afterOrder) {
      return calculateOrderBetween(beforeOrder, afterOrder);
    }
  } else if (beforeId) {
    const beforeOrder = findOrder(beforeId);
    if (beforeOrder) {
      return beforeOrder + 'n';
    }
  } else if (afterId) {
    const afterOrder = findOrder(afterId);
    if (afterOrder) {
      return calculateOrderBefore(afterOrder);
    }
  }

  return generateOrder(siblings);
}

export function buildAllSiblings(
  sections: FormSection[],
  items: FormChecklistItem[],
  parentId: string | null,
  excludeId?: string
): OrderedSibling[] {
  const sectionSiblings = sections
    .filter(
      (s) =>
        s.parentSectionId === parentId &&
        s._id !== excludeId &&
        s.uniqueKeyId !== excludeId
    )
    .map((s) => ({ id: s._id || s.uniqueKeyId, order: s.order }));

  const itemSiblings = items
    .filter(
      (i) =>
        i.parentSectionId === parentId &&
        i._id !== excludeId &&
        i.uniqueKeyId !== excludeId
    )
    .map((i) => ({ id: i._id || i.uniqueKeyId, order: i.order }));

  return [...sectionSiblings, ...itemSiblings];
}

const SCROLL_BLOCK_OFFSET = 80;

export function createChecklistItem(
  parentSectionId: string | null = null,
  existingItems: FormChecklistItem[] = []
): FormChecklistItem {
  const item = createTemplateDraftChecklistItem(parentSectionId, existingItems);

  return {
    ...item,
    errors: {
      summary: undefined,
      description: undefined,
      answers: [],
    },
    graphicUploader: undefined,
  };
}

export function createSection(
  parentSectionId: string | null = null,
  existingSections: FormSection[] = []
): FormSection {
  const section = createTemplateDraftSection(parentSectionId, existingSections);

  return {
    ...section,
    errors: {
      title: undefined,
      description: undefined,
    },
    graphicUploader: undefined,
  };
}

export function canAddItem(checklist: FormChecklistItem[]): boolean {
  const lastItem = checklist.slice(-1)[0];
  return !summaryValidation(lastItem?.summary);
}

function findScrollableElement(node: Element | null): Element | null {
  if (node == null) {
    return null;
  }

  if (node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    return findScrollableElement(node.parentNode as Element | null);
  }
}

function scrollToElement(element: Element | null): void {
  const scrollable = findScrollableElement(element);

  if (element && scrollable) {
    scrollable.scrollBy({
      top: element.getBoundingClientRect().top - SCROLL_BLOCK_OFFSET,
      behavior: 'smooth',
    });
  }
}

export const SUMMARY_DOM_ELEMENT_ID = 'summary';
export const PROCESSES_DOM_ELEMENT_ID = 'processes';
export const GENERAL_NORM_DOM_ELEMENT_ID = 'generalNorm';
export const CHECKLIST_PREFIX_DOM_ELEMENT_ID = 'checklist-';
export const CHECKLIST_ITEM_DOM_ELEMENT_ID_PREFIX = 'checklist-item-';

export function getChecklistItemDomElementId(itemId: string): string {
  return `${CHECKLIST_ITEM_DOM_ELEMENT_ID_PREFIX}${itemId}`;
}

export function scrollToFirstError(
  summaryError: ErrorPresentation,
  processError: ErrorPresentation,
  descriptionError: ErrorPresentation,
  items: FormChecklistItem[],
  itemErrors: ChecklistItemError[],
  checklistValidationFocus: ChecklistItemValidationFocus | null
): void {
  if (summaryError) {
    scrollToElement(document.querySelector(`#${SUMMARY_DOM_ELEMENT_ID}`));
    return;
  }

  if (processError) {
    scrollToElement(
      document.querySelector(`#${PROCESSES_DOM_ELEMENT_ID}`)
    );
    return;
  }

  if (descriptionError) {
    scrollToElement(
      document.querySelector(`#${GENERAL_NORM_DOM_ELEMENT_ID}`)
    );
    return;
  }

  const focus =
    checklistValidationFocus ??
    findFirstChecklistValidationFocus(items, itemErrors);

  if (focus) {
    window.setTimeout(() => {
      scrollToElement(
        document.getElementById(getChecklistItemDomElementId(focus.itemId))
      );
    }, 0);
  }
}

export function isEmptyChangeInObject(
  prev: CreateInspectionTemplateErrors,
  validationError: ErrorPresentation,
  key: 'summary' | 'description' | 'process'
): boolean {
  return (
    prev[key] === validationError ||
    prev[key]?.helperText?.id === validationError?.helperText?.id
  );
}

function isCreationView(url: string): boolean {
  return url.includes('/create');
}

export function chooseTitle(url: string): { id: string } {
  return isCreationView(url)
    ? { id: 'inspection_template_wizard_create_template' }
    : { id: 'inspection_template_wizard_edit_template' };
}

export function checkIndentificableListEquality(
  prev: Identificable[],
  curr: Identificable[]
): boolean {
  if (prev.length !== curr.length) {
    return false;
  }
  return prev.every((prevProcess) =>
    curr.find((currProcess) => prevProcess._id === currProcess._id)
  );
}

const baseSettings = {
  ...ATTACHMENTS_UPLOADER_PRESET,
  baseUrl: '',
  dataQaPrefix: 'general-applied-standards-',
  atomicChanges: true,
};
export function createGUSettings(id?: string): CONFIG_TYPE {
  return id
    ? {
        ...baseSettings,
        baseUrl: createInspectionTemplateGeneralNormDocumentsBaseUrl(id),
      }
    : baseSettings;
}
